<template>
  <div class="screen">
    <div class="logo-wrapper">
      <img class="logo" src="./assets/RA-Logo.png" alt="Rockwell Automation">
    </div>
    <div class="page-title">
      Low Voltage Drives Energy
      <br/>
      Efficiency Comparison Calculator
    </div>
    <div class="comparison-wrapper">
      <div class="comparison-group">
        <div class="group-title">Allen-Bradley Drive</div>
        <div class="detail-title">Input Drive Details</div>
        <div class="required-description">denotes required field</div>
        <div class="input-group-wrapper">
          <InputRowWithDropdown
              :inputValue='ABDrivePowerRating'
              inputTitle='Drive Power Rating'
              inputNotation='kW'
              fieldName="ABDrivePowerRating"
              @updateFormData="updateFormData"
              :inputNotationOptions="inputNotationOptions"
              @input="updatePowerOption($event)"
              :selectedUnit="selectedPowerOption"
          />
          <InputRow
              :inputValue='ABDutyCycle'
              inputTitle='Duty Cycle'
              inputNotation='%'
              fieldName="ABDutyCycle"
              @updateFormData="updateFormData"
          />
          <InputRow
              :inputValue='ABDriveLifespan'
              inputTitle='Estimated Drive Lifespan'
              inputNotation='yrs'
              fieldName="ABDriveLifespan"
              @updateFormData="updateFormData"
          />
          <InputRow
              :inputValue='ABEnergyCost'
              inputTitle='Energy Cost'
              inputNotation='$'
              fieldName="ABEnergyCost"
              @updateFormData="updateFormData"
          />
          <InputRow
              :inputValue='ABDriveEfficiency'
              inputTitle='Drive Efficiency (IEC61800-9-2 value or similar)'
              inputNotation='%'
              fieldName="ABDriveEfficiency"
              @updateFormData="updateFormData"
          />
          <InputRowWithDropdown
              :inputValue='ABAssociatedCO2'
              inputTitle='Associated CO2 per kWh'
              inputNotation='Lb/kWh'
              fieldName="ABAssociatedCO2"
              @updateFormData="updateFormData"
              :inputNotationOptions="associatedCO2InputNotationOptions"
              @input="updateAssociatedCO2UnitOption($event)"
              :selectedUnit="selectedAssociatedCO2UnitOption"
          />
          <div class="calculate-spacer"></div>
          <div class="calculated-wrapper">
            <h3 class="detail-title">Calculated Savings</h3>
            <CalculatedSavings
                :calculationResults="ABCalculatedSavings"
                :isFormDirty="isFormDirty"
                :selectedAssociatedCO2UnitOption="selectedAssociatedCO2UnitOption"
            />
          </div>
        </div>
      </div>
      <div class="comparison-group">
        <div class="group-title">Comparison Drive</div>
        <div class="detail-title">Input Drive Details</div>
        <div class="required-description">denotes required field</div>
        <div class="input-group-wrapper">
          <InputRowWithDropdown
              :inputValue='CompDrivePowerRating'
              inputTitle='Drive Power Rating'
              inputNotation='kW'
              fieldName="CompDrivePowerRating"
              @updateFormData="updateFormData"
              :inputNotationOptions="inputNotationOptions"
              @input="updatePowerOption($event)"
              :selectedUnit="selectedPowerOption"
          />
          <InputRow
              :inputValue='CompDutyCycle'
              inputTitle='Duty Cycle'
              inputNotation='%'
              fieldName="CompDutyCycle"
              @updateFormData="updateFormData"
          />
          <InputRow
              :inputValue='CompDriveLifespan'
              inputTitle='Estimated Drive Lifespan'
              inputNotation='yrs'
              fieldName="CompDriveLifespan"
              @updateFormData="updateFormData"
          />
          <InputRow
              :inputValue='CompEnergyCost'
              inputTitle='Energy Cost'
              inputNotation='$'
              fieldName="CompEnergyCost"
              @updateFormData="updateFormData"
          />
          <InputRow
              :inputValue='CompDriveEfficiency'
              inputTitle='Drive Efficiency (IEC61800-9-2 value or similar)'
              inputNotation='%'
              fieldName="CompDriveEfficiency"
              @updateFormData="updateFormData"
          />
          <InputRowWithDropdown
              :inputValue='CompAssociatedCO2'
              inputTitle='Associated CO2 per kWh'
              inputNotation='Lb/kWh'
              fieldName="CompAssociatedCO2"
              @updateFormData="updateFormData"
              :inputNotationOptions="associatedCO2InputNotationOptions"
              @input="updateAssociatedCO2UnitOption($event)"
              :selectedUnit="selectedAssociatedCO2UnitOption"
          />
        </div>
        <div class="calculate-spacer"></div>
        <div class="calculated-wrapper">
          <h3 class="detail-title">Calculated Savings</h3>
          <CalculatedSavings
              :calculationResults="CompCalculatedSavings"
              :isFormDirty="isFormDirty"
              :selectedAssociatedCO2UnitOption="selectedAssociatedCO2UnitOption"
          />
        </div>
      </div>
      <div class="middle-calculate-wrapper">
        <img class="first-arrow" src="./assets/ra-triangle-arrow.svg" alt="Rockwell Automation">

        <button
            class="middle-calculate-button"
            @click="calculateSavings"
        >
          CALCULATE SAVINGS
        </button>
      </div>
    </div>
    <div class="required-description-bottom">denotes required field</div>
  </div>
</template>

<script>

import InputRow from '@/components/InputRow';
import CalculatedSavings from '@/components/CalculatedSavings';
import InputRowWithDropdown from '@/components/InputRowWithDropdown';

export default {
  components: {
    InputRowWithDropdown,
    InputRow,
    CalculatedSavings
  },
  data() {
    return {
      ABDrivePrice: 110,
      ABDrivePowerRating: 100,
      ABDutyCycle: 90,
      ABDriveLifespan: 15,
      ABEnergyCost: .10,
      ABDriveEfficiency: 99.7,
      ABAssociatedCO2: 0.2,
      CompDrivePrice: 222,
      CompDrivePowerRating: 100,
      CompDutyCycle: 90,
      CompDriveLifespan: 15,
      CompEnergyCost: .10,
      CompDriveEfficiency: 98.2,
      CompAssociatedCO2: 0.2,
      ABCalculatedSavings: {
        co2Creation: 0,
        energyConsumption: 0,
        energyCost: 0,
        driveEfficiency: 0,
        efficiencySavings: 0,
        isDriveGreen: false,
        isEfficiencyGreen: false,
        isEfficiencyWhite: false
      },
      CompCalculatedSavings: {
        co2Creation: 0,
        energyConsumption: 0,
        energyCost: 0,
        driveEfficiency: 0,
        efficiencySavings: 0,
        isDriveGreen: false,
        isEfficiencyGreen: false,
        isEfficiencyWhite: false
      },
      totalCostDifference: 0,
      reducedTotalInvestment: 0,
      isFormDirty: true,
      inputNotationOptions: ['kW', 'HP'],
      selectedPowerOption: 'kW',
      associatedCO2InputNotationOptions: ['Lb/kWh', 'kg/kWh'],
      selectedAssociatedCO2UnitOption: 'Lb/kWh'
    };
  },
  methods: {
    updateFormData(field, value) {
      this[field] = Number(value);
      this.isFormDirty = true;
    },
    updatePowerOption(value) {
      this.selectedPowerOption = value;
    },
    updateAssociatedCO2UnitOption(value) {
      this.selectedAssociatedCO2UnitOption = value;
    },
    calculateSavings() {
      const compEnergyConsumption = getLifetimeConsumption(this.CompDrivePowerRating, this.CompDriveEfficiency, this.CompDutyCycle, this.CompDriveLifespan);
      const compAssociatedCO2 = compEnergyConsumption * this.CompAssociatedCO2;
      const compLifetimeCost = compEnergyConsumption * this.CompEnergyCost;
      const compDriveEfficiency = this.CompDriveEfficiency;
      const compLifetimeInefficiencyCost = getLifetimeEnergyCost(this.CompDriveLifespan, this.CompDutyCycle, this.CompDrivePowerRating, this.CompEnergyCost, this.CompDriveEfficiency);

      const abEnergyConsumption = getLifetimeConsumption(this.ABDrivePowerRating, this.ABDriveEfficiency, this.ABDutyCycle, this.ABDriveLifespan);
      const abLifetimeCost = abEnergyConsumption * this.ABEnergyCost;
      const abAssociatedCO2 = abEnergyConsumption * this.ABAssociatedCO2;
      const abDriveEfficiency = this.ABDriveEfficiency;
      const abLifetimeInefficiencyCost = getLifetimeEnergyCost(this.ABDriveLifespan, this.ABDutyCycle, this.ABDrivePowerRating, this.ABEnergyCost, this.ABDriveEfficiency);

      this.CompCalculatedSavings = {
        energyConsumption: compEnergyConsumption,
        energyCost: compLifetimeInefficiencyCost,
        efficiencySavings: compLifetimeInefficiencyCost - abLifetimeInefficiencyCost,
        co2Creation: compAssociatedCO2,
        driveEfficiency: compDriveEfficiency
      };

      this.ABCalculatedSavings = {
        energyConsumption: abEnergyConsumption,
        energyCost: abLifetimeInefficiencyCost,
        efficiencySavings: abLifetimeInefficiencyCost - compLifetimeInefficiencyCost,
        co2Creation: abAssociatedCO2,
        driveEfficiency: abDriveEfficiency
      };

      this.totalCostDifference = abLifetimeCost - compLifetimeCost;
      this.reducedTotalInvestment = (abLifetimeCost + this.ABDrivePrice) - (compLifetimeCost + this.CompDrivePrice);
      this.isFormDirty = false;

      const isCompDriveHigher = (this.CompCalculatedSavings.driveEfficiency > this.ABCalculatedSavings.driveEfficiency);
      const isCompEfficiencyHigher = (this.CompCalculatedSavings.efficiencySavings > this.ABCalculatedSavings.efficiencySavings);
      const isCompEfficiencyLower = (this.CompCalculatedSavings.efficiencySavings < this.ABCalculatedSavings.efficiencySavings);

      const isAbDriveHigher = (this.ABCalculatedSavings.driveEfficiency > this.CompCalculatedSavings.driveEfficiency);
      const isAbEfficiencyHigher = (this.ABCalculatedSavings.efficiencySavings > this.CompCalculatedSavings.efficiencySavings);
      const isAbEfficiencyLower = (this.ABCalculatedSavings.efficiencySavings < this.CompCalculatedSavings.efficiencySavings);

      Object.assign(this.CompCalculatedSavings, {
        isDriveGreen: isCompDriveHigher,
        isEfficiencyGreen: isCompEfficiencyLower,
        isEfficiencyWhite: isCompEfficiencyHigher
      });

      Object.assign(this.ABCalculatedSavings, {
        isDriveGreen: isAbDriveHigher,
        isEfficiencyGreen: isAbEfficiencyLower,
        isEfficiencyWhite: isAbEfficiencyHigher
      });
    }
  }
};

const getLifetimeConsumption = (drivePowerRating, driveEfficiency, dutyCycle, driveLifespan) => {
  const hoursPerYear = 8760;
  const driveLifetimeHours = driveLifespan * (dutyCycle / 100) * hoursPerYear;
  const driveEnergyDeliveredToMotor = drivePowerRating * driveLifetimeHours;

  return (driveEnergyDeliveredToMotor / driveEfficiency) * 100;
};

const getLifetimeEnergyCost = (driveLifespan, dutyCycle, drivePowerRating, energyCost, driveEfficiency) => {
  const hoursPerYear = 24 * 365;
  const driveLifetimeHours = driveLifespan * (dutyCycle / 100) * hoursPerYear;
  const driveEnergyDeliveredToMotor = drivePowerRating * driveLifetimeHours;
  const lifetimeEnergyConsumption = (driveEnergyDeliveredToMotor / driveEfficiency) * 100;

  return energyCost * lifetimeEnergyConsumption;
};
</script>


<style lang="scss">

@font-face {
  font-family: 'Barlow';
  font-style: normal;
  font-weight: 400;
  src: local('Barlow'), url(./fonts/Barlow-Regular.ttf) format('woff2');
}

* {
  box-sizing: border-box !important;
}

body {
  font-family: 'Barlow', serif;
  color: var(--text-color);
}

:root {
  --orange: rgba(245, 128, 37, 1);
  --red: rgba(205, 22, 63, 1);
  --shadow-grey: #b3b3b3;
  --text-color: #43434d;
  --green: #6cc644;
  --white: #FFFFFF;
}

.logo {
  height: 80px;
}

.logo-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
}

.page-title {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 50px;
  line-height: 1.1;
  padding: 50px 0 150px;
  color: white;
  background: rgb(205, 22, 63);
  background: linear-gradient(90deg, rgba(205, 22, 63, 1) 0%, var(--orange) 50%);
  text-align: center;
}

.comparison-wrapper {
  max-width: 1000px;
  margin: -100px auto 80px;
  width: 100%;
  height: 80rem;
  background: linear-gradient(
          to bottom,
          var(--orange) 0rem,
          var(--orange) 42rem,
          white 42rem,
          white 100rem
  );
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
}

.comparison-group {
  border-radius: 10px;
  margin: -10px -15px;
  padding: 10px;
  width: 50%;
  background-color: white;
  background-image: url('./assets/chevron.svg');
  background-size: 110%;
  background-repeat: no-repeat;
  background-position: -25px -40px;
  box-shadow: 0 0 5px var(--shadow-grey);
}

.group-title {
  font-size: 36px;
  margin-bottom: 40px;
  text-align: center;
}

.detail-title {
  color: var(--orange);
  text-align: center;
  font-size: 20px;
}

.required-description {
  text-align: right;
  font-size: 12px;
  margin-right: 15%;
  margin-top: 10px;
  margin-bottom: -6px;
}

.required-description::before {
  content: "*";
  color: var(--red);
}

input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input:focus-visible {
  outline: none;
}

.row-title::after {
  content: "*";
  color: var(--red);
}

.calculate-spacer {
  height: 200px;
}

.calculated-wrapper {
  background-color: white;
  box-shadow: 0 0 5px var(--shadow-grey);
  border-radius: 5px;
  width: 80%;
  margin: 0 auto;
  padding: 20px;
}

.sum {
  color: var(--orange);
  font-size: 36px;
}

.first-arrow {
  height: 100px;
}

.middle-calculate-wrapper {
  position: absolute;
  top: 630px;
  display: flex;
  width: 100%;
  text-align: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.middle-calculate-button {
  background: linear-gradient(90deg, rgba(205, 22, 63, 1) 0%, var(--orange) 100%);
  width: 300px;
  height: 50px;
  border: 0;
  color: white;
  border-radius: 5px;
  margin-top: 10px;
}


.input-group-wrapper {
  padding: 0 30px;
}

.required-description-bottom {
  font-size: 12px;
  margin-left: 14%;
  margin-top: -60px;
  margin-bottom: 40px;
}

.required-description-bottom::before {
  content: "*";
  color: var(--red);
}
</style>
